import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { FiUser, FiLock } from 'react-icons/fi';
import {
  DashboardContainer,
  BurgerButton,
  MobileDashboardHeader,
  MobileHeaderBurger,
  MobileHeaderLogo,
  MobileHeaderAddButton,
  MobileHeaderAddMenu,
  MobileHeaderAddOption,
  SidebarOverlay,
  Sidebar,
  SidebarTop,
  SidebarLogoButton,
  SidebarLogoSJ,
  SidebarLogoMate,
  SidebarTitle,
  SidebarDivider,
  NavList,
  NavItem,
  NavSubItem,
  NavSubList,
  NavSpacer,
  SidebarBottom,
  LogoutButton,
  SettingsIconButton,
  SidebarFooterLine,
  MainContent,
} from '../CoachDashboard/styles';
import { athleteAPI } from '../../services/api';
import styled from 'styled-components';
import JumperHome from './JumperHome';
import JumperTrainingSessions from './JumperTrainingSessions';
import JumperCompetitionSessions from './JumperCompetitionSessions';
import JumperTrainingPlanner from './JumperTrainingPlanner';
import JumperEquipment from './JumperEquipment';
import JumperBananaCup from './JumperBananaCup';
import JumperAnalytics from './JumperAnalytics';
import AccountSettings from '../AccountSettings/AccountSettings';
import JumperChat from '../Chat/JumperChat';
import LoadingSpinner from '../Common/LoadingSpinner';

const UserIcon = FiUser as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const LockIcon = FiLock as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const LockOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 320px;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  overflow-y: auto;

  @media (max-width: 768px) {
    left: 0;
    z-index: 50; /* below BurgerButton (101) and Sidebar (100) so user can open menu */
  }

  @media (max-width: 1024px) and (orientation: landscape) {
    z-index: 50;
  }
`;

const LockContent = styled.div`
  text-align: center;
  max-width: 500px;
`;

const LockIconLarge = styled.div`
  font-size: 5rem;
  color: var(--accent-orange);
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LockTitle = styled.h2`
  font-size: 2rem;
  font-weight: 900;
  color: var(--text-dark);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: -0.02em;
`;

const LockText = styled.p`
  font-size: 1.1rem;
  color: var(--text-gray);
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.6;
`;

const LockButton = styled.button`
  padding: 1rem 2.5rem;
  background: var(--accent-orange);
  color: var(--text-light);
  border: none;
  border-radius: var(--radius-full);
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(255, 150, 0, 0.3);

  &:hover {
    background: var(--accent-orange-dark);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 150, 0, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BlurredContent = styled.div<{ $isLocked: boolean }>`
  filter: ${props => props.$isLocked ? 'blur(4px)' : 'none'};
  transition: filter 0.3s;
  width: 100%;
  min-height: 100%;
`;

type NavSection = {
  id: string;
  label: string;
  path: string;
  subItems?: NavSection[];
};

const navSections: NavSection[] = [
  { id: 'home', label: 'Home', path: '/jumper/home' },
  { id: 'training-planner', label: 'Training Planner', path: '/jumper/training-planner' },
  { id: 'training-sessions', label: 'Training Sessions', path: '/jumper/training-sessions' },
  { id: 'competition-sessions', label: 'Competition Sessions', path: '/jumper/competition-sessions' },
  { id: 'equipment', label: 'Equipment', path: '/jumper/equipment' },
  { id: 'banana-cup', label: 'Banana Cup 🍌', path: '/jumper/banana-cup' },
  { id: 'analytics', label: 'Analytics', path: '/jumper/analytics' },
];

const JumperDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { canAccessPage, isLoading: isSubscriptionLoading } = useSubscriptionAccess();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(() => {
    // Don't set active section if we're on account-settings page
    if (location.pathname === '/jumper/account-settings') {
      return '';
    }
    // Check for AI chat first
    if (location.pathname === '/jumper/ai-chat') {
      return 'ai-chat';
    }
    // Check for analytics
    if (location.pathname.startsWith('/jumper/analytics')) {
      return 'analytics';
    }
    const match = navSections.find(section => location.pathname.includes(section.id));
    return match ? match.id : 'home';
  });

  useEffect(() => {
    // Don't set active section if we're on account-settings page
    if (location.pathname === '/jumper/account-settings') {
      setActiveSection('');
      return;
    }
    // Check for AI chat first
    if (location.pathname === '/jumper/ai-chat') {
      setActiveSection('ai-chat');
      return;
    }
    // Check for analytics
    if (location.pathname.startsWith('/jumper/analytics')) {
      setActiveSection('analytics');
      return;
    }
    const match = navSections.find(section => location.pathname.includes(section.id));
    setActiveSection(match ? match.id : 'home');
  }, [location.pathname]);

  const handleNavigation = (section: NavSection) => {
    setActiveSection(section.id);
    navigate(section.path);
    // Close sidebar on mobile after navigation
    setIsSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsSidebarOpen(false);
  };

  const handleSettingsClick = () => {
    navigate('/jumper/account-settings');
    setIsSidebarOpen(false);
  };

  const isSettingsActive = location.pathname === '/jumper/account-settings';

  // Close sidebar when clicking overlay
  const handleOverlayClick = () => {
    setIsSidebarOpen(false);
  };

  // Determine if current page is locked
  const getCurrentPageId = (): string | null => {
    if (location.pathname === '/jumper/ai-chat') return 'ai-chat';
    if (location.pathname.startsWith('/jumper/analytics')) return 'analytics';
    if (location.pathname === '/jumper/account-settings') return null; // Settings always accessible
    const match = navSections.find(section => location.pathname.includes(section.id));
    return match ? match.id : null;
  };

  const currentPageId = getCurrentPageId();
  // Only check if page is locked after subscription data has loaded
  const isCurrentPageLocked = !isSubscriptionLoading && currentPageId ? !canAccessPage(currentPageId) : false;

  // Mobile header visibility on scroll (mobile only)
  const [mobileHeaderVisible, setMobileHeaderVisible] = useState(true);
  const [isMobileView, setIsMobileView] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  const [mobileAddMenuOpen, setMobileAddMenuOpen] = useState(false);
  const [jumperAthletePlan, setJumperAthletePlan] = useState<string | null>(null);
  const lastScrollYRef = React.useRef(0);
  const [scrollContainer, setScrollContainer] = React.useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!user?.athleteId) {
      setJumperAthletePlan(null);
      return;
    }
    athleteAPI.getById(user.athleteId).then((a: { subscription?: { plan?: string } }) => {
      setJumperAthletePlan((a?.subscription?.plan || '').toLowerCase() || null);
    }).catch(() => setJumperAthletePlan(null));
  }, [user?.athleteId]);

  const handleMobileAddOption = (action: string) => {
    setMobileAddMenuOpen(false);
    const plan = jumperAthletePlan || '';
    const hasAnyPaidPlan = ['hs60', 'hs90', 'hs130', 'hs240'].includes(plan);
    const hasBananaCupAccess = ['hs90', 'hs130', 'hs240'].includes(plan);
    if (action === 'training') {
      navigate('/jumper/training-sessions', { state: { openNewSessionForm: true } });
      return;
    }
    if (action === 'planner' && hasBananaCupAccess) {
      navigate('/jumper/training-planner', { state: { openNewPlanForm: true } });
      return;
    }
    if (action === 'competition' && hasAnyPaidPlan) {
      navigate('/jumper/competition-sessions', { state: { openNewSessionForm: true } });
      return;
    }
    if (action === 'equipment' && hasAnyPaidPlan) {
      navigate('/jumper/equipment', { state: { openNewEquipmentForm: true } });
      return;
    }
    if (action === 'banana' && hasBananaCupAccess) {
      navigate('/jumper/banana-cup', { state: { openNewLeagueForm: true } });
      return;
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobileView || !scrollContainer) return;
    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      if (scrollTop > 60 && scrollTop > lastScrollYRef.current) {
        setMobileHeaderVisible(false);
      } else if (scrollTop <= 60 || scrollTop < lastScrollYRef.current) {
        setMobileHeaderVisible(true);
      }
      lastScrollYRef.current = scrollTop;
    };
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isMobileView, scrollContainer]);

  // Prevent body scroll when overlay is shown and lock scroll position
  useEffect(() => {
    if (isCurrentPageLocked || isSubscriptionLoading) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isCurrentPageLocked, isSubscriptionLoading]);

  return (
    <DashboardContainer>
      {isMobileView && (
        <MobileDashboardHeader $visible={mobileHeaderVisible}>
          <MobileHeaderBurger
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </MobileHeaderBurger>
          <MobileHeaderLogo
            onClick={() => {
              navigate('/jumper/home');
              setIsSidebarOpen(false);
            }}
            aria-label="Go to dashboard home"
          >
            <SidebarLogoSJ as="span">sj</SidebarLogoSJ>
            <SidebarLogoMate as="span">Mate</SidebarLogoMate>
          </MobileHeaderLogo>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <MobileHeaderAddButton
              type="button"
              onClick={() => setMobileAddMenuOpen(prev => !prev)}
              title={mobileAddMenuOpen ? 'Close menu' : 'Add new'}
              aria-label={mobileAddMenuOpen ? 'Close menu' : 'Add new'}
            >
              +
            </MobileHeaderAddButton>
            {mobileAddMenuOpen && (
              <MobileHeaderAddMenu>
                <MobileHeaderAddOption onClick={() => handleMobileAddOption('training')}>Add new training session</MobileHeaderAddOption>
                {['hs60', 'hs90', 'hs130', 'hs240'].includes(jumperAthletePlan || '') && (
                  <>
                    <MobileHeaderAddOption onClick={() => handleMobileAddOption('competition')}>Add new competition session</MobileHeaderAddOption>
                    <MobileHeaderAddOption onClick={() => handleMobileAddOption('equipment')}>Add new equipment</MobileHeaderAddOption>
                  </>
                )}
                {['hs90', 'hs130', 'hs240'].includes(jumperAthletePlan || '') && (
                  <>
                    <MobileHeaderAddOption onClick={() => handleMobileAddOption('planner')}>Plan new training session</MobileHeaderAddOption>
                    <MobileHeaderAddOption onClick={() => handleMobileAddOption('banana')}>Create new Banana Cup</MobileHeaderAddOption>
                  </>
                )}
              </MobileHeaderAddMenu>
            )}
          </div>
        </MobileDashboardHeader>
      )}
      {!isMobileView && (
        <BurgerButton
          $isOpen={isSidebarOpen}
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </BurgerButton>
      )}
      <SidebarOverlay $isOpen={isSidebarOpen} onClick={handleOverlayClick} />
      <Sidebar $isOpen={isSidebarOpen}>
        <SidebarTop>
          <SidebarLogoButton
            onClick={() => {
              navigate('/jumper/home');
              setIsSidebarOpen(false);
            }}
            type="button"
            aria-label="Go to dashboard home"
          >
            <SidebarLogoSJ>sj</SidebarLogoSJ>
            <SidebarLogoMate>Mate</SidebarLogoMate>
          </SidebarLogoButton>
          <SidebarTitle>SKI JUMPER DASHBOARD</SidebarTitle>
          <SidebarDivider />
        </SidebarTop>
        <NavList $gap="0.6rem">
          {['home', 'training-sessions', 'training-planner', 'competition-sessions', 'equipment', 'banana-cup', 'analytics'].map((id, index) => {
            const section = navSections.find(s => s.id === id);
            if (!section) return null;
            const isFirstItem = index === 0;
            const hasAccess = canAccessPage(section.id);
            return (
              <NavItem
                key={section.id}
                $active={activeSection === section.id}
                $isFirstItem={isFirstItem}
                onClick={() => handleNavigation(section)}
                style={{
                  opacity: hasAccess ? 1 : 0.5,
                  cursor: hasAccess ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                {!hasAccess && (
                  <LockIcon style={{
                    position: 'absolute',
                    right: '1rem',
                    fontSize: '0.9rem',
                    color: 'var(--accent-orange)'
                  }} />
                )}
                {section.label}
              </NavItem>
            );
          })}
          <NavItem
            $active={activeSection === 'ai-chat'}
            $isFirstItem={false}
            onClick={() => {
              if (!canAccessPage('ai-chat')) return;
              navigate('/jumper/ai-chat');
              setIsSidebarOpen(false);
            }}
            style={{
              opacity: canAccessPage('ai-chat') ? 1 : 0.5,
              cursor: canAccessPage('ai-chat') ? 'pointer' : 'not-allowed',
              position: 'relative'
            }}
          >
            {!canAccessPage('ai-chat') ? (
              <LockIcon style={{ 
                position: 'absolute', 
                right: '1rem', 
                fontSize: '0.9rem',
                color: 'var(--accent-orange)'
              }} />
            ) : null}
            AI MATE
          </NavItem>
          <NavSpacer />
        </NavList>
        <SidebarBottom>
          <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
          <SettingsIconButton 
            onClick={handleSettingsClick} 
            title="Account Settings"
            style={{
              borderColor: isSettingsActive ? 'var(--accent-orange)' : 'var(--card-border)',
              color: isSettingsActive ? 'var(--accent-orange)' : 'var(--text-gray)',
              background: isSettingsActive ? 'var(--bg-sidebar)' : 'var(--bg-primary)',
            }}
          >
            <UserIcon />
          </SettingsIconButton>
        </SidebarBottom>
        <SidebarFooterLine />
      </Sidebar>
      <MainContent ref={(el) => setScrollContainer(el)} style={{ position: 'relative', overflow: (isCurrentPageLocked || isSubscriptionLoading) ? 'hidden' : 'auto' }}>
        <BlurredContent $isLocked={isCurrentPageLocked}>
          <Routes>
            <Route path="home" element={<JumperHome />} />
            <Route path="training-sessions" element={<JumperTrainingSessions />} />
            <Route path="training-planner" element={<JumperTrainingPlanner />} />
            <Route path="competition-sessions" element={<JumperCompetitionSessions />} />
            <Route path="equipment" element={<JumperEquipment />} />
            <Route path="banana-cup" element={<JumperBananaCup />} />
            <Route path="analytics/*" element={<JumperAnalytics />} />
            <Route path="ai-chat" element={<JumperChat />} />
            <Route path="account-settings" element={<AccountSettings />} />
            <Route path="*" element={<JumperHome />} />
          </Routes>
        </BlurredContent>
        {isSubscriptionLoading && (
          <LockOverlay>
            <LoadingSpinner />
          </LockOverlay>
        )}
        {!isSubscriptionLoading && isCurrentPageLocked && (
          <LockOverlay>
            <LockContent>
              <LockIconLarge>
                <LockIcon style={{ fontSize: '5rem' }} />
              </LockIconLarge>
              <LockTitle>
                {currentPageId === 'ai-chat' ? 'Coming Soon' : 'This Feature is Locked'}
              </LockTitle>
              <LockText>
                {currentPageId === 'ai-chat'
                  ? 'AI Mate is not quite ready yet. In the meantime, check out our other plans on the Pricing page.'
                  : currentPageId === 'analytics'
                  ? 'Upgrade to HS130 or HS240 plan to unlock Analytics and track your performance with detailed insights.'
                  : (currentPageId === 'training-planner' || currentPageId === 'equipment')
                  ? 'Upgrade to HS90, HS130, or HS240 plan to unlock this feature and access advanced training tools.'
                  : 'Upgrade to HS60, HS90, HS130, or HS240 plan to unlock this feature and access advanced training tools.'
                }
              </LockText>
              <LockButton onClick={() => navigate('/pricing')}>
                View Pricing Plans
              </LockButton>
            </LockContent>
          </LockOverlay>
        )}
      </MainContent>
    </DashboardContainer>
  );
};

export default JumperDashboard;

